import { revertDeviceModel } from '@/shared/lib';
import _ from 'lodash';
import { createClient } from '@/shared/api';
import type { CallCenterStudiesQuery, CallCenterStudiesQueryVariables } from '@/shared/api';
import AWAITING_UPLOAD_DEVICES_QUERY from '../queries/awaitingUploadDevices';

/**
 * Loads the call-center studies whose device recordings are still awaiting upload. Deep-clones the filter and rewrites its model to the legacy identifier the backend expects, queries AWAITING_UPLOAD_DEVICES_QUERY with the filter and page limit, and throws the first GraphQL error message when no studies came back.
 *
 * @param filter - Filter criteria, whose model is converted to its legacy name.
 * @param limit - Maximum number of records to return.
 * @returns Promise resolving to the awaiting-upload call-center studies.
 */
const fetchAwaitingUploadDevices = async (
  filter: CallCenterStudiesQueryVariables['filter'],
  limit = 10,
): Promise<CallCenterStudiesQuery['callCenterStudies']> => {
  const client = createClient();
  const clonedFilter = _.cloneDeep(filter);
  if (!_.isEmpty(clonedFilter)) {
    clonedFilter.model = revertDeviceModel(clonedFilter.model);
  }
  const result = await client.query<CallCenterStudiesQuery, CallCenterStudiesQueryVariables>({
    query: AWAITING_UPLOAD_DEVICES_QUERY,
    variables: {
      filter: clonedFilter,
      limit,
    },
  });
  const { data, errors } = result;
  const { callCenterStudies } = data;
  if (_.isNil(callCenterStudies) && errors?.length) {
    throw new Error(errors[0].message);
  }
  return callCenterStudies;
};

export default fetchAwaitingUploadDevices;
